/* One entry per service desk this platform can connect to. **Adding a connector is one entry here.**
 *
 * Everything the screens need about a provider lives in this record: its mark, its accent, what it
 * calls its credential, what a destination needs beyond an address, and where an operator goes to
 * mint a token. Nothing else in `features/itsm/` branches on `ItsmProvider` — the screens read the
 * registry, so a third connector is a row in `PROVIDERS` and a path in `MARKS`, and no layout
 * changes. That is the same reasoning `CAPABILITIES` and `build_connectors` use on the backend,
 * where `lumiq-arch` invariant 7 fails a build containing `if provider == JIRA`.
 *
 * **`satisfies Record<ItsmProvider, …>` is what makes it total**, so a member added to the generated
 * enum is a compile error in this file rather than a provider that renders with no mark and no
 * accent. The alternative — a lookup with a `??` fallback — is how §9's `verdictTone` stripped a
 * failed row of its badge.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * ON THE MARKS, because one of the two is not what it looks like.
 *
 * **Jira's is the real Atlassian mark**, the exact path from Simple Icons 16.34.0 (`siJira`), whose
 * own source is Atlassian's logo library. CC0, so vendoring it is permitted, and vendored rather
 * than fetched because hard rule 13 means an air-gapped install has no CDN — and rather than taking
 * the 12 MB `simple-icons` dependency into a bank's wheelhouse for one path.
 *
 * **ServiceNow has no mark, and that is a finding rather than an omission.** Simple Icons carries
 * 3,464 icons and none of them is ServiceNow; brands are removed on request and ServiceNow's own
 * guidelines restrict use of theirs. So the choice was between drawing one from memory — a wrong
 * logo, which is worse than no logo and is somebody's trademark — and a monogram that is plainly
 * Lumiq's own typographic device. The monogram wins, and `markKind` says which a reader is looking
 * at so nobody later "fixes" the monogram by inventing the real thing.
 *
 * A deployment that holds a licence for the real asset can replace one entry. That is the other
 * reason the mark is a slot rather than a hard-coded element.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * This file carries one `react-refresh/only-export-components` warning, knowingly and without a
 * disable comment. The rule's advice is to split the constants out of a file that also exports a
 * component — which here would put `PROVIDERS` in one file and `MARKS` in another, and make
 * "adding a connector is one entry" into two entries in two places, on the file whose entire
 * argument is that it is one. The cost it buys back is Fast Refresh in dev: editing the registry
 * forces a reload rather than a hot update. Silencing it per file is what CLAUDE.md §6 bans, so it
 * stays visible and this paragraph is the reason it is not acted on.
 */

import type { ReactElement } from 'react';

import type { AuthType, ItsmProvider } from '@/shared/api/generated/itsm';

export type MarkKind = 'brand' | 'monogram';

export interface ProviderProfile {
  /** The vendor's own capitalisation. Not a copy key: "ServiceNow" is spelled that way in every
   * language, and a translation key would invite somebody to change one. */
  readonly label: string;
  /** Which credential it takes, so a form asks for the right thing without branching on the name. */
  readonly authType: AuthType;
  /** Whether a connection needs a project and issue type beyond an address. */
  readonly needsDestination: boolean;
  /** What the address looks like, shown as a placeholder rather than as prose. */
  readonly addressExample: string;
  /** Where an operator goes to mint the credential. A sentence, not a link: an air-gapped install
   * has no outbound browsing, and a dead external link is worse than a described path. */
  readonly credentialHint: string;
  readonly markKind: MarkKind;
  /** The provider's own colour on a light surface, or a Lumiq token where no licensed brand colour
   * is available. */
  readonly accent: string;
  /** The same on a dark surface, and **required rather than optional**.
   *
   * A brand colour is chosen against the background its owner publishes it on, so it does not
   * survive a theme flip: Atlassian's `#0052CC` measures **2.72:1** against this platform's dark
   * page, which is a mark a reader cannot see. An optional field falling back to `accent` would
   * have made that the default for every connector added later — the silent-degradation shape
   * CLAUDE.md records `verdictTone`'s `default:` taking. Required means a third connector cannot
   * be added without somebody having looked at it on both surfaces.
   *
   * A provider whose accent is a Lumiq token declares the same token twice, because `var(--accent)`
   * already flips. That reads as redundant and is the point: it makes the providers that need two
   * different values visible as the exception they are. */
  readonly accentDark: string;
}

export const PROVIDERS = {
  servicenow: {
    label: 'ServiceNow',
    authType: 'basic',
    needsDestination: false,
    addressExample: 'https://acme.service-now.com',
    credentialHint:
      'Create an integration user with the itil role, then use its username and password.',
    markKind: 'monogram',
    // **Not a ServiceNow brand colour**, deliberately. Guessing a hex is the same mistake as
    // drawing the mark: close is wrong, and wrong is somebody's brand. This is the platform's own
    // accent token, which also keeps the monogram reading as Lumiq's device rather than theirs —
    // and, being a semantic token, it is the one value here that flips with the theme by itself.
    accent: 'var(--accent)',
    accentDark: 'var(--accent)',
  },
  jira: {
    label: 'Jira',
    authType: 'api_token',
    needsDestination: true,
    addressExample: 'https://acme.atlassian.net',
    credentialHint:
      'Create an API token on your Atlassian account, then use the account email with it.',
    markKind: 'brand',
    // Atlassian's own blue, carried with the mark it belongs to. Published for a light surface,
    // where it measures 6.08:1.
    accent: '#0052CC',
    // The lighter blue Atlassian uses on dark surfaces. **What this entry has to satisfy is a
    // measurement rather than a citation**: against this platform's dark page it is 6.77:1 where
    // `#0052CC` is 2.72:1 — read off a real dark boot in a browser, not computed from the hex.
    accentDark: '#579DFF',
  },
} as const satisfies Record<ItsmProvider, ProviderProfile>;

export function profileFor(provider: ItsmProvider): ProviderProfile {
  return PROVIDERS[provider];
}

/** Every provider, in registry order. The order the connect step offers them in. */
export const PROVIDER_ORDER = Object.keys(PROVIDERS) as readonly ItsmProvider[];

/* The marks.
 *
 * `viewBox="0 0 24 24"` on both, so one CSS rule sizes every provider and a third connector
 * inherits the sizing rather than needing its own. `currentColor` rather than the brand hex baked
 * in, so a mark on a dark surface, inside a pressed button, or in a disabled row takes the ink of
 * its context — the accent is applied by the *caller* through the CSS custom property below.
 */

function JiraMark(): ReactElement {
  return (
    <path
      /* Simple Icons 16.34.0, `siJira`. Source: Atlassian's logo library, via
       * https://atlassian.design/resources/logo-library. CC0, vendored rather than fetched for the
       * air-gap reason in this file's header. Do not edit this path: it is the mark, and a
       * hand-adjusted trademark is a worse problem than a misaligned one. */
      d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.001 1.001 0 0 0 23.013 0Z"
    />
  );
}

function ServiceNowMonogram(): ReactElement {
  /* **A monogram, not a logo**, for the reason in this file's header: no permissively licensed
   * ServiceNow mark exists and drawing one from memory would ship somebody's trademark, wrongly.
   *
   * Deliberately geometric rather than imitative — a ring with the initials — so it reads as a
   * placeholder a licensed asset replaces, and never as an attempt at the real thing. Drawn with
   * `<circle>` and `<text>` rather than a path, which is also what makes it trivial to copy for the
   * next connector in the same position.
   */
  return (
    <>
      <circle
        cx="12"
        cy="12"
        r="10.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <text
        x="12"
        y="12"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="8.5"
        fontWeight="700"
        letterSpacing="-0.4"
        fill="currentColor"
      >
        SN
      </text>
    </>
  );
}

const MARKS = {
  servicenow: ServiceNowMonogram,
  jira: JiraMark,
} as const satisfies Record<ItsmProvider, () => ReactElement>;

export interface ProviderMarkProps {
  readonly provider: ItsmProvider;
  /** Tint with the provider's accent. Off for a mark inside a pressed control or a disabled row,
   * where the surrounding ink is what the reader should follow. */
  readonly tinted?: boolean;
}

/** One provider's mark, sized and tinted by CSS rather than by props.
 *
 * **`aria-hidden` with no label, always.** The mark never appears without the provider's name
 * beside it — a logo is a visual shorthand for text that is already on the page, and labelling it
 * would make a screen reader say "ServiceNow ServiceNow". WCAG 1.4.1 is satisfied by the name, not
 * by the mark, which is also why no status is ever carried by the accent alone.
 */
export function ProviderMark({
  provider,
  tinted = true,
}: ProviderMarkProps): ReactElement {
  const Mark = MARKS[provider];
  return (
    <svg
      viewBox="0 0 24 24"
      className={tinted ? 'lq-pmark lq-pmark--tinted' : 'lq-pmark'}
      /* Custom properties rather than `color` directly, matching `StatusBadge`'s per-tone ink: the
         CSS rule decides *how* the accent is applied and this supplies only the values, so a change
         to how marks are tinted is one rule rather than one edit per call site.

         **Both surfaces are supplied and CSS chooses**, rather than this component reading the
         theme. A component that read `data-theme` would be a second place the theme is decided, and
         it would be wrong inside a subtree that sets the attribute itself — which is exactly how a
         theme toggle is built here. */
      style={
        tinted
          ? {
              ['--pmark-ink' as string]: PROVIDERS[provider].accent,
              ['--pmark-ink-dark' as string]: PROVIDERS[provider].accentDark,
            }
          : undefined
      }
      aria-hidden="true"
      focusable="false"
    >
      <Mark />
    </svg>
  );
}
